import { Injectable } from '@angular/core'

import { Router } from '@angular/router'
import { Kayttaja } from '@lemon/core-firebase/model/kayttaja'
import { ActionAfterAuthenticationLemonaid, NetsEnvironment, StrongAuthenticationSessionHistory, StrongAuthenticationSessionStep } from '@lemon/core-firebase/model/tunnistaminen'
import { LocalStorageService } from 'app/_jaettu-angular/service/local-storage.service'
import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'
import { EnvironmentType } from '../../app.environment'
import { FirebaseLemonaid } from './firebase-lemonaid.service'

type NetsAuthenticationResult = 'success' | 'error' | 'error-cancel'
interface TunnistautuminenClientSession {
  action: ActionAfterAuthenticationLemonaid
  state: string
  sessionAvain: string
  startedAtMillis: number
}

@Injectable()
export class TunnistautuminenService {
  private _currentSessionKey = 'tunnistauminen-session'
  private _tunnistautuminenState: NetsAuthenticationResult = null
  private _tunnistautuminenAction: ActionAfterAuthenticationLemonaid = null

  constructor(
    private _localStorageService: LocalStorageService,
    private _router: Router,
    private _timestampService: TimestampService,
    private _firebaseLemonaid: FirebaseLemonaid,
  ) {}

  navigateByAction(action: ActionAfterAuthenticationLemonaid, data?: unknown) {
    this._tunnistautuminenAction = action
    if (action === 'continue-welcome') {
      this._router.navigate(['/'])
    } else if (action === 'continue-standalone-dialog') {
      this._router.navigate(['/'])
    } else if (action === 'continue-payment-approval') {
      this._router.navigate(['/maksut'])
    } else if (action === 'continue-aineiston-lataus') {
      this._router.navigate(['aineiston-lataus'], { state: data })
    } else {
      throw new Error('unknown action ' + action)
    }
  }

  setTunnistautuminenState(state: NetsAuthenticationResult) {
    this._tunnistautuminenState = state
  }

  getTunnistautuminenState(): NetsAuthenticationResult {
    return this._tunnistautuminenState
  }

  getTunnistautuminenAction(): ActionAfterAuthenticationLemonaid {
    return this._tunnistautuminenAction
  }

  /**
   * Kirjoittaa tunnistautumissession tiedot SessionStorageen. Huomaa asynkroninen ja uudelleenyrittävä toteutustapa.
   * Tarkoituksena varmistua, että tieto on todellisuudessa tallentunut ennen kun suoritusta jatketaan eteenpäin, koska
   * data on kriittistä tunnistautumisen kannalta.
   */
  public async startClientSideAuthenticationSession(action: ActionAfterAuthenticationLemonaid, sessionAvain: string, state: string) {
    return this._localStorageService.setToSessionStorageUntilVerified<TunnistautuminenClientSession>(this._currentSessionKey, {
      action: action,
      state: state,
      sessionAvain: sessionAvain,
      startedAtMillis: new Date().getTime(),
    })
  }

  public getClientSideAuthenticationSession(): TunnistautuminenClientSession {
    return this._localStorageService.getFromSessionStorage<TunnistautuminenClientSession>(this._currentSessionKey)
  }

  public endClientSideAuthenticationSession() {
    this._localStorageService.deleteFromSessionStorage(this._currentSessionKey)
    this._tunnistautuminenState = null
    this._tunnistautuminenAction = null
  }

  getNetsEnvironment(environment: EnvironmentType): NetsEnvironment {
    if (environment === EnvironmentType.PRODUCTION) {
      return 'prod'
    } else if (environment === EnvironmentType.BETA) {
      return 'beta'
    } else if (environment === EnvironmentType.DEV) {
      return 'dev'
    } else if (environment === EnvironmentType.LOCAL_DEV) {
      return 'local-dev'
    }
    throw new Error('Unknown environment ' + environment)
  }

  saveHistory(kayttaja: Kayttaja, step: StrongAuthenticationSessionStep, details: string, env: NetsEnvironment): Promise<any> {
    if (kayttaja) {
      const historyData: StrongAuthenticationSessionHistory = {
        asiakasAvain: kayttaja.asiakasAvain,
        kayttajaAvain: kayttaja.avain,
        logTime: this._timestampService.now(),
        step: step,
        details: details,
        env: env,
      }
      const avain = this._firebaseLemonaid.firestoreCreateId()
      const uri = 'customers/' + kayttaja.asiakasAvain + '/customers-users/' + kayttaja.avain + '/user-auth-history/' + avain
      return this._firebaseLemonaid.firestoreSetData(uri, historyData)
    } else {
      return new Promise(resolve => resolve(true))
    }
  }
}
